/**
 * Tooltip Manager
 * Enhanced tooltip behavior for footer action buttons
 */

import { __vueEvent } from "../constant";
import { EVENTS } from "../enums";

export class TooltipManager {
  private activeTooltip: HTMLElement | null = null;
  private hideTimeout: number | null = null;
  private escKeyHandler: (e: KeyboardEvent) => void;
  private mouseMoveHandler: (e: MouseEvent) => void;
  private isMouseInTooltipZone: boolean = false;
  private prefix: string = '';

  // Property to track buttons for cleanup
  private managedButtons: HTMLElement[] = [];

  constructor(prefix: string = '') {
    this.prefix = prefix;
    this.escKeyHandler = this.handleEscKey.bind(this);
    this.mouseMoveHandler = this.handleMouseMove.bind(this);
    
    document.addEventListener('keydown', this.escKeyHandler);
    document.addEventListener('mousemove', this.mouseMoveHandler);
  }

  /**
   * Setup enhanced tooltip behavior for a button
   */
  public setupTooltip(button: HTMLElement): void {
    if (!button) return;

    const mouseEnterHandler = () => this.showTooltip(button);
    const mouseLeaveHandler = () => {
      button.classList.remove(this.prefix + 'tooltip-esc-dismissed');
      this.scheduleHide(button);
    };
    const focusHandler = () => this.showTooltip(button);
    const blurHandler = () => this.scheduleHide(button);

    button.addEventListener('mouseenter', mouseEnterHandler);
    button.addEventListener('mouseleave', mouseLeaveHandler);
    button.addEventListener('focus', focusHandler);
    button.addEventListener('blur', blurHandler);

    // Store handlers for cleanup
    (button as any).__tooltipHandlers = {
      mouseEnterHandler,
      mouseLeaveHandler,
      focusHandler,
      blurHandler
    };
    
    // Add to tracking array
    this.managedButtons.push(button);
  }

  /**
   * Track mouse movement globally to detect tooltip zone
   */
private handleMouseMove(e: MouseEvent): void {
  if (!this.activeTooltip) {
    this.isMouseInTooltipZone = false;
    return;
  }
  const buttonRect = this.activeTooltip.getBoundingClientRect();
  const tooltipElement = this.activeTooltip.querySelector('[data-tooltip]') as HTMLElement;
  
  // Check if we're already in the tooltip zone
  if (this.isMouseInTooltipZone) {
    // If we're in the tooltip zone, check against both button and tooltip
    if (tooltipElement) {
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const isOverTooltip = (
        e.clientX >= tooltipRect.left - 10 &&
        e.clientX <= tooltipRect.right + 10 &&
        e.clientY >= tooltipRect.top - 10 &&
        e.clientY <= tooltipRect.bottom + 10
      );
      
      if (isOverTooltip) {
        this.cancelHide();
        return;
      }
    }
  }
  // Check if mouse is over the button
  const isOverButton = (
    e.clientX >= buttonRect.left - 10 &&
    e.clientX <= buttonRect.right + 10 &&
    e.clientY >= buttonRect.top - 10 &&
    e.clientY <= buttonRect.bottom + 10
  );
  // If we have a tooltip element, check if mouse is over it
  let isOverTooltip = false;
  if (tooltipElement) {
    const tooltipRect = tooltipElement.getBoundingClientRect();
    isOverTooltip = (
      e.clientX >= tooltipRect.left - 10 &&
      e.clientX <= tooltipRect.right + 10 &&
      e.clientY >= tooltipRect.top - 10 &&
      e.clientY <= tooltipRect.bottom + 10
    );
  }
  this.isMouseInTooltipZone = isOverButton || isOverTooltip;
  if (this.isMouseInTooltipZone) {
    this.cancelHide();
  } else {
    this.scheduleHide(this.activeTooltip);
  }
}

  /**
   * Show tooltip immediately
   */
  private showTooltip(button: HTMLElement): void {
    this.cancelHide();

    // Hide previous tooltip
    if (this.activeTooltip && this.activeTooltip !== button) {
      this.hideTooltip(this.activeTooltip);
    }

    // Clear any ESC-dismissed state so tooltip shows again on new hover/focus
    button.classList.remove(this.prefix + 'tooltip-esc-dismissed');

    // Show new tooltip
    button.classList.add('tooltip-active');
    this.activeTooltip = button;
    this.isMouseInTooltipZone = true;
  }

  /**
   * Schedule tooltip to hide after delay
   */
  private scheduleHide(button: HTMLElement): void {
    this.cancelHide();

    // Longer delay to allow mouse movement to tooltip
    this.hideTimeout = window.setTimeout(() => {
      // Double-check if mouse is still in tooltip zone
      if (!this.isMouseInTooltipZone) {
        this.hideTooltip(button);
      }
    }, 500); // Increased delay from 500ms
  }

  /**
   * Hide tooltip immediately
   */
  private hideTooltip(button: HTMLElement): void {
    button.classList.remove('tooltip-active');
    
    if (this.activeTooltip === button) {
      this.activeTooltip = null;
      this.isMouseInTooltipZone = false;
    }
  }

  /**
   * Cancel scheduled hide
   */
  private cancelHide(): void {
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  /**
   * Handle ESC key to dismiss tooltip — works for both focus and hover states.
   */
  private handleEscKey(e: KeyboardEvent): void {
    if ((e.key === 'Escape' || e.key === 'Esc') && this.activeTooltip) {
      e.preventDefault();
      e.stopPropagation();

      const button = this.activeTooltip;
      const isFocused = button === document.activeElement;

      // Always add suppression class — overrides CSS :hover so the tooltip
      // stays hidden while the mouse remains over the button.
      // Removed automatically on mouseleave so the next hover works normally.
      button.classList.add(this.prefix + 'tooltip-esc-dismissed');

      // If the button was focused, blur it so CSS :focus rule also clears.
      if (isFocused) {
        button.blur();
        __vueEvent.emit(EVENTS.FocusInputArea);
      }

      this.hideTooltip(button);
    }
  }

  /**
   * Cleanup all event listeners
   */
  public destroy(): void {
    if (this.activeTooltip) {
      this.hideTooltip(this.activeTooltip);
    }
    
    this.cancelHide();
    document.removeEventListener('keydown', this.escKeyHandler);
    document.removeEventListener('mousemove', this.mouseMoveHandler);
    
    // Clean up all button listeners
    this.managedButtons.forEach(btn => this.cleanupButton(btn));
    this.managedButtons = [];
  }
  
  /**
   * Remove all listeners from a specific button
   */
  private cleanupButton(button: HTMLElement): void {
    const handlers = (button as any).__tooltipHandlers;
    if (!handlers) return;

    button.removeEventListener('mouseenter', handlers.mouseEnterHandler);
    button.removeEventListener('mouseleave', handlers.mouseLeaveHandler);
    button.removeEventListener('focus', handlers.focusHandler);
    button.removeEventListener('blur', handlers.blurHandler);

    // Remove the custom property to free memory
    delete (button as any).__tooltipHandlers;
  }
}